import React, {useContext, useRef, useState} from "react";
import {FormFeedback, Input, Row, Col, FormGroup} from "reactstrap";
import {isEmpty, isset} from "../inc/Helpers";
import ReCAPTCHA from "react-google-recaptcha";
import Constants from "../inc/Constants";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faSpinner} from "@fortawesome/pro-solid-svg-icons/faSpinner";
import LangContext from "../providers/LangContext";
import {post, postCustomEndpoint} from "../inc/Http";
import Swal from "sweetalert2";

const initialState = {
    name: "",
    streetname: "",
    house_number: "",
    house_number_ext: "",
    postcode: "",
    city: "",
    email: "",
    phone: "",
    recaptcha: "",
};

const LeadToolForm = ({...props}) => {
    const { lang } = useContext(LangContext);
    const recaptchaRef = useRef();
    const [{
        name,
        streetname,
        house_number,
        house_number_ext,
        postcode,
        city,
        email,
        phone,
    }, setState] = useState(initialState);

    const [privacy, setPrivacy] = useState(false)
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [recaptcha, setRecaptcha] = useState('');

    const onChange = e => {
        const {name, value} = e.target;
        setState(prevState => ({...prevState, [name]: value}));
    };

    const submit = () => {
        let woningwaardeRequest;
        setLoading(true);
        const formData = {
            name,
            streetname,
            house_number,
            house_number_ext,
            postcode,
            city,
            email,
            phone,
            privacy,
            recaptcha,
            company: document.head.querySelector('meta[name="company_id"]').content,
            _token: document.head.querySelector('meta[name="csrf-token"]').content,
        };

        woningwaardeRequest = (props.referrer === 'site') ?
            post(`${props.referrerID}/woningwaarde`, formData) :
            postCustomEndpoint(`lead-report/${props.referrerID}/woningwaarde`, formData);

        woningwaardeRequest.then(function (response) {
            Swal.fire({
                text: response.data.success,
                icon: 'success',
                timer: 5000
            }).then(() => {
                setState(initialState)
                setLoading(false);
                if(props.redirect) {
                    let redirectUrl = document.head.querySelector('meta[name="company_url"]').content;
                    window.location.href = 'http://' + redirectUrl.replace(/(^http(s{1})(\W{3}))/g, '');
                }
            })
        }).catch(function (error) {
            setLoading(false);
            recaptchaRef.current.reset();
            let errors = error.response.data.errors || {};
            setErrors(errors);

            if (isEmpty(errors)) {
                // throw server error
                Swal.fire({
                    icon: "error",
                    text: error.response.data,
                })
            }
            setLoading(false);
        });

    }

    const submitButtonText = loading ? 'Een moment geduld aub' : 'Gratis indicatie aanvragen';

    return (
        <FormGroup className={"container content woningwaarde"}>
            {props.companyLogo && (
                <Row className="mb-3" style={{marginTop: '-2rem', backgroundColor: document.head.querySelector('meta[name="header_color"]').content, minHeight: '3rem'}}>
                    <Col sm={7} className="align-self-sm-center order-1 order-sm-0 text-center text-sm-left">
                        <p className="subtitle mb-sm-0">Gratis woningwaarde indicatie</p>
                    </Col>
                    <Col sm={5} className="align-self-sm-center order-0 order-sm-1 text-center text-sm-right">
                        <img src={props.companyLogo.content} alt="Logo" className="logo"/>
                    </Col>
                </Row>
            )}
            <Row>
                <Col xs={12} md={8}>
                    <p className="lead">
                        Wilt u gratis een woningwaarde indicatie ontvangen?
                        Hiermee krijgt u een inschatting van de waarde van uw
                        woning, mede op basis van in uw omgeving verkochte
                        woningen.
                    </p>
                    <p className="lead">De voordelen:</p>
                    <span className="ml-2">
                        <i className="fa fa-check"></i> Gratis rapportage;
                    </span>
                    <br />
                    <span className="ml-2">
                        <i className="fa fa-check"></i> Direct in uw inbox;
                    </span>
                    <br />
                    <span className="ml-2">
                        <i className="fa fa-check"></i> Op basis van
                        daadwerkelijke verkoopprijzen in de buurt;
                    </span>
                    <br />
                    <span className="ml-2">
                        <i className="fa fa-check"></i> Zonder enige
                        verplichtingen.
                    </span>
                    <br />
                    <br />
                    <p className="lead">Direct ontvangen?</p>
                    <p>Vul dan hieronder uw gegevens in.</p>

                    <FormGroup row>
                        <Col sm={12}>
                            <label className="mb-0">
                                {lang.get("sites.name")}:
                            </label>
                            <Input
                                invalid={isset(errors, "name")}
                                placeholder={lang.get("sites.name")}
                                name="name"
                                value={name}
                                onChange={onChange}
                            />
                            <FormFeedback>{errors.name}</FormFeedback>
                        </Col>
                    </FormGroup>
                    <FormGroup row>
                        <Col xs={12} sm={6}>
                            <label className="mb-0">
                                {lang.get("sites.streetname")}:
                            </label>
                            <Input
                                invalid={isset(errors, "streetname")}
                                placeholder={lang.get(
                                    "sites.streetname"
                                )}
                                name="streetname"
                                type="text"
                                value={streetname}
                                onChange={onChange}
                            />
                            <FormFeedback>{errors.streetname}</FormFeedback>
                        </Col>
                        <Col sm={3}>
                            <label className="mb-0">
                                {lang.get("sites.house_number")}:
                            </label>
                            <Input
                                type="tel"
                                invalid={isset(errors, "house_number")}
                                placeholder={lang.get("portal.number")}
                                name="house_number"
                                value={house_number}
                                onChange={onChange}
                            />
                            <FormFeedback>{errors.house_number}</FormFeedback>
                        </Col>
                        <Col sm={3}>
                            <label className="mb-0">
                                {lang.get(
                                    "portal.street_number_addition"
                                )}
                                :
                            </label>
                            <Input
                                type="text"
                                invalid={isset(errors, "house_number_ext")}
                                placeholder={lang.get(
                                    "sites.number_addition_abbreviation"
                                )}
                                name="house_number_ext"
                                value={house_number_ext}
                                onChange={onChange}
                            />
                            <FormFeedback>
                                {errors.house_number_ext}
                            </FormFeedback>
                        </Col>
                    </FormGroup>
                    <FormGroup row>
                        <Col xs={6} sm={6}>
                            <label className="mb-0">
                                {lang.get("sites.zipcode")}:
                            </label>
                            <Input
                                invalid={isset(errors, "postcode")}
                                placeholder={lang.get("sites.zipcode")}
                                name="postcode"
                                value={postcode}
                                onChange={onChange}
                            />
                            <FormFeedback>{errors.postcode}</FormFeedback>
                        </Col>
                        <Col xs={6} sm={6}>
                            <label className="mb-0">
                                {lang.get("sites.city")}:
                            </label>
                            <Input
                                type="text"
                                invalid={isset(errors, "city")}
                                placeholder={lang.get("portal.city")}
                                name="city"
                                value={city}
                                onChange={onChange}
                            />
                            <FormFeedback>{errors.city}</FormFeedback>
                        </Col>
                    </FormGroup>
                    <FormGroup>
                        <label className="mb-0">
                            {lang.get("sites.email")}:
                        </label>
                        <Input
                            type="email"
                            invalid={isset(errors, "email")}
                            placeholder={lang.get("sites.email")}
                            name="email"
                            value={email}
                            onChange={onChange}
                        />
                        <FormFeedback>{errors.email}</FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <label className="mb-0">
                            {lang.get("sites.phone")}:
                        </label>
                        <Input
                            type="tel"
                            invalid={isset(errors, "phone")}
                            placeholder={lang.get("sites.phone")}
                            name="phone"
                            value={phone}
                            onChange={onChange}
                        />
                        <FormFeedback>{errors.phone}</FormFeedback>
                    </FormGroup>
                    <div className="form-group mb-0">
                        <label className="mb-0 ml-4">
                            <Input
                                invalid={isset(errors, "privacy")}
                                type="checkbox"
                                onChange={e => {
                                    setPrivacy(e.target.value === "on");
                                }}
                                check={privacy.toString()}
                                name="privacy"
                            />{" "}
                            <p
                                dangerouslySetInnerHTML={{
                                    __html: lang.get(
                                        "sites.check_privacy_statement",
                                        { termsLink: props.terms }
                                    )
                                }}
                            />
                        </label>
                        <FormFeedback className="d-block mt-0">
                            {errors.privacy}
                        </FormFeedback>
                    </div>
                    <div className="form-group">
                        <ReCAPTCHA
                            sitekey={Constants.RECAPTHCA_KEY.siteKey}
                            onChange={setRecaptcha}
                            theme={props.theme === "v1" ? "dark" : "light"}
                            ref={recaptchaRef}
                        />{" "}
                        {isset(errors, errors.recaptcha) && (
                            <div className="invalid-feedback d-block mt-0">
                                {errors.recaptcha}
                            </div>
                        )}
                    </div>
                    <div
                        className="btn btn-primary btn-block mb-3"
                        onClick={submit}
                    >
                        {loading && <FontAwesomeIcon icon={faSpinner} spin />}{" "}
                        {submitButtonText}
                    </div>
                </Col>
                <div className="col-12 col-lg-4 d-none d-lg-block">
                    <img
                        src={"/images/voorbeeld-woningwaarde-indicatie.jpg"}
                        className={"img-fluid"}
                    />
                    <p className={"figure-caption text-center"}>
                        Een voorbeeld van een gratis woningwaarde indicatie.
                    </p>
                </div>
            </Row>
        </FormGroup>
    );
}

export default LeadToolForm;
